<script setup lang="ts">
import { computed } from 'vue';

type Variant = 'default' | 'dark' | 'light';
type Position = 'top' | 'bottom' | 'left' | 'right';
type Size = 'standard' | 'wide';

type Props = {
  text: string;
  placement?: Position;
  size?: Size;
  tooltipId?: string;
  arrow?: boolean;
  focus?: boolean;
  light?: boolean;
  variant?: Variant;
  enabled?: boolean;
};

const {
  text,
  placement = 'bottom',
  size = 'standard',
  tooltipId = 'tooltip__text',
  arrow = true,
  focus = true,
  variant = 'default',
  enabled = true,
} = defineProps<Props>();

const tooltipTextClasses = computed(() => ({
  tooltip__text: true,
  [`tooltip__text--${variant}`]: true,
  [`tooltip__text--${placement}`]: true,
  'tooltip__text--arrow': arrow,
  [`tooltip__text--${size}`]: true,
  'tooltip__text--open-focus': focus,
}));
</script>

<template>
  <div v-if="enabled" class="tooltip">
    <slot />
    <span :class="tooltipTextClasses" role="tooltip" :id="tooltipId" inert>
      {{ text }}
    </span>
  </div>
  <slot v-else />
</template>

<style scoped>
.tooltip {
  --tooltip-color: rgb(109 109 109);
  --tooltip-border: rgb(109 109 109);
  --font-color: white;
  position: relative;
  display: inline-block;
}

.tooltip__text {
  visibility: hidden;
  opacity: 0;
  transition: opacity 400ms;
  color: var(--font-color);
  font-size: 0.8rem;
  text-align: center;
  padding: var(--gap-sm);
  border: 1px solid var(--tooltip-border);
  border-radius: var(--radius-sm);
  min-width: 120px;
  width: max-content;
  background: var(--tooltip-color);
  position: absolute;
  z-index: 1000000000;
}

.tooltip__text--dark {
  --tooltip-color: black;
  --tooltip-border: black;
  --font-color: white;
}

.tooltip__text--light {
  --tooltip-color: white;
  --tooltip-border: black;
  --font-color: black;
}

.tooltip__text--standard {
  max-width: 300px;
}

.tooltip__text--wide {
  max-width: 500px;
}

@media (hover: hover) {
  .tooltip:hover .tooltip__text {
    visibility: visible;
    opacity: 1;
  }
}

:slotted(*):focus + .tooltip__text--open-focus {
  visibility: visible;
  opacity: 1;
}

.tooltip__text--top {
  inset-block-end: 130%;
  inset-inline-start: 50%;
  transform: translateX(-50%);
}

.tooltip__text--bottom {
  inset-block-start: 130%;
  inset-inline-start: 50%;
  transform: translateX(-50%);
}

.tooltip__text--left {
  inset-block-start: 50%;
  transform: translateY(-50%);
  inset-inline-end: 100%;
  margin-right: 10px;
}

.tooltip__text--right {
  inset-block-start: 50%;
  transform: translateY(-50%);
  inset-inline-start: 100%;
  margin-left: 10px;
}

/* ::before contains the outside, thin border */
/* ::after contains the color of the triangle (what looks like the fill of the triangle) */
.tooltip__text--arrow::before,
.tooltip__text--arrow::after {
  content: ' ';
  position: absolute;
  border-style: solid;
}

.tooltip__text::before {
  border-color: var(--tooltip-border) transparent transparent transparent;
  border-width: 6px;
}

.tooltip__text::after {
  border-color: var(--tooltip-color) transparent transparent transparent;
  border-width: 5px;
}

.tooltip__text--left::before,
.tooltip__text--left::after {
  inset-block-start: 50%;
  inset-inline-start: 100%;
  top: 50%;
  transform: translateY(-50%);
}

.tooltip__text--left::before {
  border-color: transparent transparent transparent var(--tooltip-border);
}

.tooltip__text--left::after {
  border-color: transparent transparent transparent var(--tooltip-color);
}

.tooltip__text--right::before,
.tooltip__text--right::after {
  inset-block-start: 50%;
  inset-inline-end: 100%;
  top: 50%;
  transform: translateY(-50%);
}

.tooltip__text--right::before {
  border-color: transparent var(--tooltip-border) transparent transparent;
}

.tooltip__text--right::after {
  border-color: transparent var(--tooltip-color) transparent transparent;
}

.tooltip__text--top::before,
.tooltip__text--top::after {
  inset-block-start: 100%;
  inset-inline-start: 50%;
  transform: translateX(-50%);
}

.tooltip__text--top::before {
  border-color: var(--tooltip-border) transparent transparent transparent;
}

.tooltip__text--top::after {
  border-color: var(--tooltip-color) transparent transparent transparent;
}

.tooltip__text--bottom::before,
.tooltip__tex--bottomt::after {
  inset-block-end: 100%;
  inset-inline-start: 50%;
  transform: translateX(-50%);
}

.tooltip__text--bottom::before {
  border-color: transparent transparent var(--tooltip-border) transparent;
}
.tooltip__text--bottom::after {
  border-color: transparent transparent var(--tooltip-color) transparent;
}
</style>
